import { useState, useEffect } from "react";
import { ArrowLeft, Plus, Loader, Users, Phone } from "lucide-react";
import { SearchInput } from "./BroadcastAtoms";
import { AVATAR_COLORS } from "./constants";
import api from "../../services/api";
import { toast } from "react-toastify";

const AddContactsView = ({ group, existingIds, onBack, onAdded }) => {
  const [allCustomers, setAll]  = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    api.get("/customers/by-user")
      .then((r) => setAll(r.data.customers || r.data.data || []))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoading(false));
  }, []);

  const available = allCustomers.filter(
    (c) => !existingIds.has(c.id) &&
      ((c.name || "").toLowerCase().includes(search.toLowerCase()) ||
       (c.phoneNumber || "").includes(search))
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleAdd = async () => {
    if (!selected.size) return;
    setSaving(true);
    try {
      await api.post(`/group-members/add-multiple/${group.id}`, { customerIds: [...selected] });
      toast.success(`${selected.size} contact${selected.size > 1 ? "s" : ""} added`);
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to add contacts");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-shrink-0">
        <button onClick={onBack} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">Add Contacts</p>
          <p className="text-[11px] text-gray-400">{available.length} available to add</p>
        </div>
        {selected.size > 0 && (
          <button
            onClick={handleAdd}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#075e54] hover:bg-[#064c44] text-white text-xs font-medium rounded-full transition-colors"
          >
            {saving ? <Loader className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add {selected.size}
          </button>
        )}
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name or phone…" />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <Loader className="w-5 h-5 animate-spin text-gray-300" />
          </div>
        ) : available.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center px-4">
            <Users className="w-8 h-8 text-gray-200 mb-2" />
            <p className="text-sm text-gray-400">
              {allCustomers.length === existingIds.size
                ? "All your contacts are already in this group."
                : "No contacts match your search."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {available.map((c, idx) => (
              <label key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="w-4 h-4 accent-[#075e54] flex-shrink-0"
                />
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${AVATAR_COLORS[(c.id || idx) % AVATAR_COLORS.length]}`}>
                  {(c.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.name || "—"}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <p className="text-xs text-gray-400 truncate">{c.phoneNumber}</p>
                  </div>
                </div>
                {c.status === false && (
                  <span className="text-[10px] bg-red-50 text-red-400 px-1.5 py-0.5 rounded-full flex-shrink-0">inactive</span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddContactsView;
